import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { Input } from 'antd';
import { type TextAreaProps } from 'antd/es/input';
import { type TextAreaRef } from 'antd/es/input/TextArea';

const { TextArea } = Input;

export interface EnterTextareaRef {
  focus: () => void;
}

export interface EnterTextareaProps extends Omit<TextAreaProps, 'onPressEnter' | 'onChange'> {
  onEnter?: (value: string) => void;
  clearOnEnter?: boolean;
  disableEnter?: boolean;
  onChange?: (value: string) => void;
}

export const EnterTextarea = forwardRef<EnterTextareaRef, EnterTextareaProps>(
  ({ onEnter, clearOnEnter = false, disableEnter = false, value, onChange, autoFocus, ...rest }, ref) => {
    const textareaRef = useRef<TextAreaRef>(null);

    const focusTextarea = () => {
      const textarea = textareaRef.current?.resizableTextArea?.textArea;

      if (!textarea) return;

      textarea.focus();

      const length = textarea.value?.length ?? 0;

      textarea.setSelectionRange(length, length);
      textarea.scrollTop = textarea.scrollHeight;
    };

    useImperativeHandle(ref, () => ({
      focus: focusTextarea,
    }));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        if (disableEnter) return;

        const currentValue = e.currentTarget.value;

        onEnter?.(currentValue);

        if (clearOnEnter) {
          onChange?.('');
        }
      }
    };

    useEffect(() => {
      if (!autoFocus) return;

      requestAnimationFrame(focusTextarea);
    }, [autoFocus]);

    return (
      <TextArea
        {...rest}
        ref={textareaRef}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  }
);

EnterTextarea.displayName = 'EnterTextarea';
